export const PILOT_FUNNEL_EVENTS = [
  'estimate_started',
  'estimate_calculated',
  'shield_reviewed',
  'pdf_generated',
  'pdf_failed',
  'pilot_cta_clicked',
] as const;

export type PilotFunnelEvent = (typeof PILOT_FUNNEL_EVENTS)[number];

export type ProductAnalyticsEvent =
  | PilotFunnelEvent
  | 'pwa_install_cta_seen'
  | 'pwa_install_cta_clicked'
  | 'pwa_install_prompt_accepted'
  | 'pwa_install_prompt_dismissed'
  | 'pwa_installed'
  | 'ios_install_instructions_seen';

export type PilotAnalyticsRecord = {
  event: PilotFunnelEvent;
  occurredAt: number;
  eventDate: string;
  sessionId: string;
};

export type ProductAnalyticsAdapter = (event: ProductAnalyticsEvent) => void;

const PILOT_SESSION_KEY = 'prowycena_pilot_session';
const FUNNEL_EVENT_SET = new Set<string>(PILOT_FUNNEL_EVENTS);
let analyticsAdapter: ProductAnalyticsAdapter | null = null;

export function configureProductAnalytics(adapter: ProductAnalyticsAdapter | null): void {
  analyticsAdapter = adapter;
}

export function isPilotFunnelEvent(event: string): event is PilotFunnelEvent {
  return FUNNEL_EVENT_SET.has(event);
}

export function createPilotAnalyticsRecord(
  event: PilotFunnelEvent,
  occurredAt = Date.now(),
  sessionId = getOrCreatePilotSessionId()
): PilotAnalyticsRecord {
  return {
    event,
    occurredAt,
    eventDate: formatWarsawDate(occurredAt),
    sessionId,
  };
}

export function trackProductEvent(event: ProductAnalyticsEvent): void {
  analyticsAdapter?.(event);
}

function getOrCreatePilotSessionId(): string {
  if (typeof window === 'undefined') return 'server-test-session';

  try {
    const existing = window.localStorage.getItem(PILOT_SESSION_KEY);
    if (existing) return existing;

    const randomPart = window.crypto.randomUUID();
    const sessionId = `pilot_${randomPart}`;
    window.localStorage.setItem(PILOT_SESSION_KEY, sessionId);
    return sessionId;
  } catch {
    return 'pilot_storage_unavailable';
  }
}

function formatWarsawDate(timestamp: number): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(timestamp));
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}
